import React, { useMemo, useState } from 'react';
import Text from '../../shared/Text';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import styled from 'styled-components';
import PieChart from './PieChart';
import ScreenWrapper from '../../shared/ScreenWrapper';
import styles from '../../../utility/styles';
import DatePicker from './DatePicker';
import LatestList from './LatestList';
import RolloutLineChart from './RolloutLineChart';
import { compareStrings } from '../../../utility/helpers';

function GeistFonts() {
    React.useEffect(() => {
        ['https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap',
         'https://fonts.googleapis.com/css2?family=Geist+Mono:wght@400;500;600;700&display=swap'
        ].forEach(href => {
            if (!document.querySelector(`link[href="${href}"]`)) {
                const link = document.createElement('link')
                link.rel = 'stylesheet'
                link.href = href
                document.head.appendChild(link)
            }
        })
    }, [])
    return null
}

const Page = styled(Container)`
    margin-top: 48px;
    margin-bottom: 75px;
    max-width: 960px;
    font-family: ${styles.fonts.geist};
    padding: 0 24px;
    @media screen and (max-width: 992px) {
        overflow-x: hidden;
    }
`

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 36px;
`

const ChartSection = styled.div`
    background-color: ${styles.colors.darkBackground};
    border-radius: 12px;
    margin-bottom: 40px;
    overflow: hidden;
`

const ChartToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-bottom: 1px solid rgba(45, 54, 70, 0.5);
`

const ChartTabs = styled.div`
    display: flex;
    gap: 2px;
    background: rgba(45, 54, 70, 0.4);
    border-radius: 6px;
    padding: 2px;
`

const ChartTab = styled.button`
    padding: 5px 14px;
    border-radius: 5px;
    border: none;
    background: ${({ active }) => active ? 'rgba(255,255,255,0.1)' : 'transparent'};
    color: ${({ active }) => active ? styles.colors.white : styles.colors.mediumGray};
    font-family: ${styles.fonts.geist};
    font-weight: 500;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s;
    &:hover {
        color: ${styles.colors.white};
    }
`

const ModelFilters = styled.div`
    display: flex;
    gap: 4px;
`

const ModelBtn = styled.button`
    padding: 4px 12px;
    border-radius: 4px;
    border: none;
    background: ${({ active }) => active ? 'rgba(255,255,255,0.12)' : 'transparent'};
    color: ${({ active }) => active ? styles.colors.white : styles.colors.mediumGray};
    font-family: ${styles.fonts.geist};
    font-weight: 500;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.15s;
    &:hover { color: ${styles.colors.lightGray}; }
`

const ChartBody = styled.div`
    padding: 16px 20px 20px;
`

const ListHeader = styled.div`
    display: flex;
    align-items: baseline;
    margin-bottom: 8px;
`

const RivianIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 41 40" fill="none">
        <path d="M36.3118 18.8C34.5658 17 25.1203 7.265 23.3496 5.85875C22.7308 5.363 21.9283 5 20.9383 5C19.9483 5 19.1436 5.363 18.5271 5.85875C16.7571 7.265 7.30331 17 5.56331 18.8C5.41331 18.965 5.54981 19.2215 5.76431 19.2215L9.87731 19.2245C10.3063 19.2245 10.4338 19.1495 10.7038 18.86L12.3163 17.129C14.0241 15.2878 18.1888 11.2228 19.7113 9.94175C20.2828 9.46025 20.9301 9.47675 20.9391 9.47675C20.9481 9.47675 21.5953 9.46175 22.1668 9.94175C23.6893 11.2168 27.8541 15.2893 29.5618 17.129L31.1743 18.86C31.4443 19.1495 31.5718 19.2245 32.0008 19.2245L36.1138 19.2215C36.3283 19.223 36.4543 18.9643 36.3118 18.8ZM5.56331 21.1273C7.30931 22.9272 16.7548 32.6713 18.5248 34.0768C19.1428 34.5718 19.9378 34.9355 20.9361 34.9355C21.9343 34.9355 22.7308 34.5725 23.3473 34.0768C25.1173 32.6713 34.5636 22.928 36.3096 21.1273C36.4596 20.9622 36.3231 20.7057 36.1086 20.7057C35.2363 20.7057 33.2136 20.7028 31.9963 20.7028C31.5673 20.7028 31.4398 20.7778 31.1698 21.0673L29.5573 22.7975C27.8496 24.6388 23.6848 28.7038 22.1623 29.9848C21.5908 30.4662 20.9436 30.4497 20.9346 30.4497C20.9256 30.4497 20.2783 30.4648 19.7068 29.9848C18.1843 28.7098 14.0196 24.6372 12.3118 22.7975L10.6993 21.0673C10.4293 20.7778 10.3018 20.7028 9.87281 20.7028L5.75981 20.7057C5.54831 20.7057 5.42081 20.963 5.56331 21.1273ZM19.7961 23.1898L17.1351 20.552C16.9101 20.288 16.9101 19.952 16.9101 19.952C16.9101 19.952 16.8951 19.622 17.1351 19.352C17.6451 18.7557 19.0101 17.4245 19.7961 16.7143C20.1148 16.4173 20.1913 16.2298 20.1913 15.8653V11.825C20.1913 11.6623 20.0016 11.561 19.8538 11.6975C18.1168 13.3055 14.2483 17.1395 12.9516 18.6245C12.3306 19.2823 12.3891 19.952 12.3891 19.952C12.3891 19.952 12.3321 20.627 12.9516 21.2795C14.2491 22.7645 18.1266 26.5985 19.8538 28.2065C20.0083 28.3415 20.1913 28.2568 20.1913 28.0655V24.0328C20.1913 23.6578 20.1148 23.4778 19.7961 23.1822V23.1898ZM22.0798 23.1927L24.7408 20.555C24.9658 20.291 24.9658 19.955 24.9658 19.955C24.9658 19.955 24.9808 19.625 24.7408 19.355C24.2308 18.7588 22.8658 17.4275 22.0798 16.7172C21.7611 16.4202 21.6846 16.2327 21.6846 15.8682V11.8475C21.6846 11.6563 21.8668 11.5715 22.0221 11.7065C23.7591 13.3145 27.6276 17.1485 28.9243 18.6335C29.5438 19.2913 29.4868 19.961 29.4868 19.961C29.4868 19.961 29.5438 20.636 28.9243 21.2885C27.6268 22.7735 23.7493 26.6075 22.0221 28.2155C21.8676 28.3505 21.6846 28.2658 21.6846 28.0745V24.0425C21.6861 23.6675 21.7611 23.4897 22.0798 23.1927Z" fill="white" />
    </svg>
)

const TeslaIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 41 40" fill="none">
        <path fillRule="evenodd" clipRule="evenodd" d="M34.6588 9.6564C29.6588 7.4763 24.7654 6.77725 20.5 6.8128C16.2346 6.7891 11.3412 7.4763 6.34123 9.66825C5.74882 8.5782 5.5 7.92654 5.5 7.92654C10.9739 5.75829 16.0924 5.0237 20.5 5C24.9194 5.0237 30.0379 5.75829 35.5 7.91469C35.5 7.91469 35.3223 8.44787 34.6588 9.6564ZM24.7061 11.2677L20.5118 34.8815L16.2938 11.2677C12.3246 11.2677 11.0568 11.7061 10.8791 13.4952C10.8791 13.4952 8.18952 12.5 6.83881 10.462C12.1232 8.00944 17.4312 7.9028 17.4312 7.9028L20.5177 11.6634L23.6042 7.9028C23.6042 7.9028 28.9123 8.00944 34.1966 10.462C32.8459 12.5 30.1563 13.4952 30.1563 13.4952C29.9668 11.7061 28.7109 11.2677 24.7061 11.2677Z" fill="white" />
    </svg>
)

export default function SoftwareTracker({ data }) {
    const {
        make, model, make_friendly_name, vehicle_models,
        model_friendly_name, pie_chart_data, recent_versions, rollout_line_data
    } = data
    const [activeButton, setActiveButton] = useState(vehicle_models.find(({ model: m }) => compareStrings(m, model))?.model ?? 'all')
    const [chartView, setChartView] = useState('rollout')
    const [segment, setSegment] = useState(pie_chart_data.length - 1)

    const pieData = useMemo(() => {
        try {
            const arr = pie_chart_data[segment]?.versions ?? []
            return arr.map(version => ({ version: version.version, pct: version.vehicles.find(vehicle => compareStrings(vehicle?.model_name, activeButton))?.pct })).filter(({ pct }) => !!pct) ?? []
        } catch (error) {
            return []
        }
    }, [activeButton, pie_chart_data, segment])

    const days = useMemo(() => {
        try { return pie_chart_data.map(({ date }) => date) }
        catch (error) { return [] }
    }, [activeButton, data])

    const hasChartData = (rollout_line_data || []).length > 0 || (pie_chart_data || []).length > 0

    return (
        <ScreenWrapper>
            <GeistFonts />
            <Page>
                <Header>
                    {make === 'rivian' && <RivianIcon />}
                    {make === 'tesla' && <TeslaIcon />}
                    <Text.Heading variant='bold' mobileL='h4'>{make_friendly_name} Software Tracker</Text.Heading>
                </Header>

                {hasChartData && (
                    <ChartSection>
                        <ChartToolbar>
                            <ChartTabs>
                                <ChartTab active={chartView === 'rollout'} onClick={() => setChartView('rollout')}>90-Day Rollout</ChartTab>
                                <ChartTab active={chartView === 'distribution'} onClick={() => setChartView('distribution')}>Daily Snapshot</ChartTab>
                            </ChartTabs>
                            {chartView === 'distribution' && (
                                <ModelFilters>
                                    <ModelBtn active={activeButton === 'all'} onClick={() => setActiveButton('all')}>All</ModelBtn>
                                    {vehicle_models.map(({ model, model_name }) => (
                                        <ModelBtn active={activeButton === model} key={model} onClick={() => setActiveButton(model)}>{model_name}</ModelBtn>
                                    ))}
                                </ModelFilters>
                            )}
                        </ChartToolbar>
                        <ChartBody>
                            {chartView === 'rollout' ? (
                                <RolloutLineChart data={rollout_line_data || []} />
                            ) : (
                                <>
                                    <PieChart data={pieData} type={make} />
                                    <DatePicker onClick={setSegment} activeSegment={segment} {...{ days }} />
                                </>
                            )}
                        </ChartBody>
                    </ChartSection>
                )}

                <ListHeader>
                    <Text.Heading type='h3' mobileL='h6' variant='bold'>Recent Versions</Text.Heading>
                </ListHeader>
                <LatestList data={recent_versions} {...{ make_friendly_name, make }} />
            </Page>
        </ScreenWrapper>
    )
}
